import React, { FunctionComponent, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  Autocomplete,
  Box,
  Card,
  CardContent,
  Fade,
  FormControl,
  FormControlLabel,
  Grid,
  Stack,
  Switch,
  SxProps,
  Table,
  TableBody,
  TableContainer,
  TablePagination,
  TextFieldProps,
  Typography,
} from '@mui/material';
import { useConfirm } from 'material-ui-confirm';

import {
  capitalizeFirstLetter,
  checkUserPermission,
} from '../../../../../../helper';
import { useAppSelector } from '../../../../../../hooks';
import { getAuthState } from '../../../../../../store/AuthSlice';
import {
  DarkButton,
  defaultButtonStyles,
} from '../../../../../../components/V2/Buttons';
import SendInvoiceModal from './SendInvoiceModal';
import {
  adminGetInvoices,
  adminSendInvoiceMail,
  getInvoicesState,
  IInvoice,
  resetAdminInvoiceListData,
  TAdminGetInvoices,
  TInvoiceStatus,
} from '../../../../../../store/V2/InvoicesSlice';
import useTable, {
  defaultRowsPerPageOptions,
  TUseTableState,
} from '../../../../../../components/V2/Table/type1/useTable';
import TableHeadCustom, {
  THeadLabelItem,
} from '../../../../../../components/V2/Table/type1/TableHeadCustom';
import TableNoData from '../../../../../../components/V2/Empty/TableNoData';
import TableLoader from '../../../../../../components/Loaders/TableLoader';
import TablePaginationActions from '../../../../../../components/V2/Table/type1/TablePaginationActions';
import InvoiceTableRow from './InvoiceTableRow';
import ViewInvoiceModal from './ViewInvoiceModal';
import EditInvoiceStatusModal from './EditInvoiceStatusModal';
import AdminNotesModal from './AdminNotesModal';
import UserListDropdown, {
  TUserSelectOption,
} from '../../../../../../components/Inputs/admin/UserListDropdown';
import {
  invoiceStatusOptions,
  TInvoiceStatusOption,
} from '../../../../../../constants';
import {
  TPAutoCompleteArrowIconComponent,
  TPTextField,
} from '../../../../../../components/Inputs';
import { USER_PERMISSION_INDIVIDUALS } from '../../../../../../store/V2/RolesSlice';

type TState = {
  selectedStudent: TUserSelectOption | null;
  selectedStatus: TInvoiceStatusOption | null;
  invoiceNumber: string;
};

type TFilterData = {
  limit: number;
  offset: number;
  sortKey: string;
  sortValue: number;
  userId?: string;
  status?: TInvoiceStatus;
  invoiceNumber?: string;
};

const textFieldProps: TextFieldProps = {
  sx: {
    '& .MuiInputBase-root': {
      height: '52px',
    },
  },
  InputLabelProps: {
    shrink: true,
  },
};

const tableCardContainerStyles: SxProps = {
  borderRadius: '20px',
  boxShadow: '0px 14px 40px 0px #6E9DB22E',
};

const defaultListLimit = 30;
const defaultOrderBy = 'createdAt';
const defaultOrder = 'desc';
const defaultTableData: TUseTableState = {
  defaultOrderBy,
  defaultOrder,
  defaultDense: false,
  defaultCurrentPage: 0,
  defaultRowsPerPage: defaultRowsPerPageOptions[0],
  defaultSelected: [],
};

const defaultFilterData: TFilterData = {
  offset: 0,
  limit: defaultListLimit,
  sortKey: defaultOrderBy,
  sortValue: defaultOrder === 'desc' ? -1 : 1,
};

const initialState: TState = {
  selectedStudent: null,
  selectedStatus: null,
  invoiceNumber: '',
};

const tableHeaderLabels: THeadLabelItem[] = [
  {
    id: 'createdAt',
    label: 'Created At',
    sortable: true,
    width: 120,
    maxWidth: 120,
  },
  {
    id: 'invoiceNumber',
    label: 'Invoice Number',
    sortable: true,
    width: 100,
    maxWidth: 100,
  },
  {
    id: 'studentName',
    label: 'Student Name',
    sortable: false,
    width: 120,
    maxWidth: 120,
  },
  {
    id: 'paymentMethod',
    label: 'Payment Method',
    sortable: true,
    width: 100,
    maxWidth: 100,
  },
  {
    id: 'status',
    label: 'Status',
    sortable: true,
    width: 120,
    maxWidth: 120,
  },
  {
    id: 'reference',
    label: 'Reference',
    sortable: false,
    width: 150,
    maxWidth: 150,
  },
  {
    id: 'adminNotes',
    label: 'Admin Notes',
    sortable: false,
    width: 100,
    maxWidth: 100,
  },
  {
    id: 'updatedBy',
    label: 'Updated By',
    sortable: false,
  },
  {
    id: 'actions',
    label: 'Actions',
    sortable: false,
    width: 200,
    minWidth: 200,
  },
];

const InvoicesTab: FunctionComponent = () => {
  const dispatch = useDispatch();
  const confirm = useConfirm();

  // Store
  const authState = useAppSelector(getAuthState);
  const invoicesState = useAppSelector(getInvoicesState);

  // State
  const tableData = useTable(defaultTableData);
  const [state, setState] = useState<TState>({ ...initialState });
  const [filterData, setFilterData] = useState({ ...defaultFilterData });
  const [isSendInvoiceModalOpen, setSendInvoiceModalOpen] = useState(false);
  const [viewInvoiceModalData, setViewInvoiceModalData] = useState<{
    open: boolean;
    invoice?: IInvoice;
  }>({ open: false });

  const [editInvoiceModalData, setEditInvoiceModalData] = useState<{
    open: boolean;
    invoice?: IInvoice;
  }>({ open: false });

  const [viewAdminNotesModalData, setViewAdminNotesModalData] = useState<{
    open: boolean;
    invoice?: IInvoice;
  }>({ open: false });

  const authUser = authState.user;
  const createPermission = checkUserPermission(authUser, {
    adminSettings: [USER_PERMISSION_INDIVIDUALS.ADMIN_SETTINGS_INVOICES_CREATE],
  });

  let invoiceList: IInvoice[] = [];
  let totalCount = 0;
  let selectedInvoiceForAdminNotesModal: IInvoice | undefined;

  if (invoicesState.adminInvoiceListData) {
    totalCount = invoicesState.adminInvoiceListData.totalDocs || 0;

    if (
      invoicesState.adminInvoiceListData.list &&
      invoicesState.adminInvoiceListData.list.length
    ) {
      invoiceList = invoicesState.adminInvoiceListData.list;
    }
  }

  if (viewAdminNotesModalData.open && viewAdminNotesModalData.invoice) {
    selectedInvoiceForAdminNotesModal = invoiceList.find(
      (item) => item._id === viewAdminNotesModalData.invoice?._id
    );
  }

  useEffect(() => {
    getInvoiceList({ ...defaultFilterData });

    return () => {
      dispatch(resetAdminInvoiceListData());
    };
  }, []);

  const updateStateAttributes = (attributes: Partial<TState>) => {
    setState((prevState) => ({
      ...prevState,
      ...attributes,
    }));
  };

  const getInvoiceList = (filterDataAttributes: TFilterData) => {
    const apiData: TAdminGetInvoices = {
      limit: filterDataAttributes.limit,
      offset: filterDataAttributes.offset,
    };

    if (filterDataAttributes.sortKey) {
      apiData.sortKey = filterDataAttributes.sortKey;
    }

    if (filterDataAttributes.sortValue) {
      apiData.sortValue = filterDataAttributes.sortValue;
    }

    if (filterDataAttributes.userId) {
      apiData.userId = filterDataAttributes.userId;
    }

    if (filterDataAttributes.status) {
      apiData.status = filterDataAttributes.status;
    }

    if (filterDataAttributes.invoiceNumber) {
      apiData.invoiceNumber = filterDataAttributes.invoiceNumber;
    }

    dispatch(adminGetInvoices({ ...apiData }));

    setFilterData({ ...filterDataAttributes });
  };

  const handleSort = (id: string) => {
    let newSortValue = 1;

    if (filterData.sortKey === id && filterData.sortValue === 1) {
      newSortValue = -1;
    }

    getInvoiceList({
      ...filterData,
      sortValue: newSortValue,
      sortKey: id,
    });

    tableData.onSort(id);
  };

  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    page: number
  ) => {
    getInvoiceList({
      ...filterData,
      offset: page * filterData.limit,
    });

    tableData.onChangePage(event, page);
  };

  const handleRowsPerPageChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    getInvoiceList({
      ...filterData,
      offset: 0,
      limit: parseInt(event.target.value, 10),
    });

    tableData.onChangeRowsPerPage(event);
  };

  const handleResendMail = (invoice: IInvoice) => {
    confirm({
      title: 'Are you sure?',
      description: 'Do you want to resend mail?',
    }).then(() => {
      dispatch(
        adminSendInvoiceMail({
          id: invoice._id,
        })
      );
    });
  };

  const searchByInvoiceNumber = () => {
    getInvoiceList({
      ...filterData,
      invoiceNumber: state.invoiceNumber.trim().length
        ? state.invoiceNumber
        : undefined,
    });
  };

  return (
    <Fade in>
      <div>
        <Card sx={tableCardContainerStyles}>
          <CardContent style={{ padding: '20px 0' }}>
            <div style={{ padding: '20px' }}>
              <div>
                <Typography fontSize="24px" fontWeight={600}>
                  Invoices
                </Typography>
              </div>
              {createPermission ? (
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="flex-end"
                >
                  <DarkButton
                    onClick={() => setSendInvoiceModalOpen(true)}
                    sx={{ ...defaultButtonStyles }}
                  >
                    Send New Invoice
                  </DarkButton>
                </Stack>
              ) : null}
            </div>
            <div style={{ marginBottom: '20px', padding: '0 20px' }}>
              <Grid
                container
                columnSpacing="16px"
                rowSpacing="20px"
                alignItems="center"
              >
                <Grid item xs={12} sm={6} md={4} lg={3}>
                  <FormControl fullWidth>
                    <UserListDropdown
                      userTypes={['student']}
                      selectedItem={state.selectedStudent}
                      handleChange={(event, value) => {
                        updateStateAttributes({ selectedStudent: value });

                        getInvoiceList({
                          ...filterData,
                          userId: value ? value.value : undefined,
                        });
                      }}
                      textFieldProps={{
                        label: 'Student',
                        placeholder: 'Select',
                        ...textFieldProps,
                      }}
                    />
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={4} lg={3}>
                  <FormControl fullWidth>
                    <Autocomplete
                      fullWidth
                      size="small"
                      value={state.selectedStatus}
                      options={invoiceStatusOptions}
                      popupIcon={TPAutoCompleteArrowIconComponent}
                      getOptionLabel={(option) =>
                        capitalizeFirstLetter(option.label.toLocaleLowerCase())
                      }
                      onChange={(e, value) => {
                        updateStateAttributes({ selectedStatus: value });
                        getInvoiceList({
                          ...filterData,
                          status: value ? value.value : undefined,
                        });
                      }}
                      renderInput={(params) => (
                        <TPTextField
                          {...params}
                          {...textFieldProps}
                          size="small"
                          label="Status"
                          placeholder="Select"
                        />
                      )}
                    />
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={12} md={6} lg={4}>
                  <Stack direction="row" alignItems="center" gap="20px">
                    <TPTextField
                      fullWidth
                      {...textFieldProps}
                      type="number"
                      label="Invoice No"
                      placeholder="Search by invoice no"
                      value={state.invoiceNumber}
                      onChange={(event) =>
                        updateStateAttributes({
                          invoiceNumber: event.target.value,
                        })
                      }
                      onKeyDown={(e) => {
                        if (
                          e.key === 'Enter' &&
                          !(
                            !state.invoiceNumber.trim().length &&
                            !filterData.invoiceNumber?.trim().length
                          )
                        ) {
                          searchByInvoiceNumber();
                        }
                      }}
                    />
                    <DarkButton
                      variant="contained"
                      color="inherit"
                      sx={{ ...defaultButtonStyles }}
                      onClick={searchByInvoiceNumber}
                      disabled={
                        !state.invoiceNumber.trim().length &&
                        !filterData.invoiceNumber?.trim().length
                      }
                    >
                      Search
                    </DarkButton>
                  </Stack>
                </Grid>
              </Grid>
            </div>
            <TableContainer
              sx={{ position: 'relative', maxHeight: 'calc(100vh - 250px)' }}
            >
              <Table stickyHeader size={tableData.dense ? 'small' : 'medium'}>
                <TableHeadCustom
                  order={tableData.order}
                  orderBy={tableData.orderBy}
                  headLabels={tableHeaderLabels}
                  rowCount={0}
                  numSelected={tableData.selected.length}
                  onSort={handleSort}
                  // onSelectAllRows={(checked) =>
                  //   tableData.onSelectAllRows(
                  //     checked,
                  //     zoomMeetingRequestsList.map((row) => row._id)
                  //   )
                  // }
                />

                <TableBody>
                  {invoiceList.map((row) => {
                    return (
                      <InvoiceTableRow
                        key={row._id}
                        invoice={row}
                        onResendMail={() => handleResendMail(row)}
                        onView={() =>
                          setViewInvoiceModalData({ open: true, invoice: row })
                        }
                        onUpdate={() =>
                          setEditInvoiceModalData({ open: true, invoice: row })
                        }
                        viewAdminNotes={() =>
                          setViewAdminNotesModalData({
                            open: true,
                            invoice: row,
                          })
                        }
                      />
                    );
                  })}
                  {invoicesState.status !== 'loading' && !invoiceList.length ? (
                    <TableNoData title="No new requests are available" />
                  ) : null}
                  {invoicesState.status === 'loading' && !invoiceList.length ? (
                    <TableLoader />
                  ) : null}
                </TableBody>
              </Table>
            </TableContainer>
            <Box sx={{ position: 'relative' }}>
              <TablePagination
                rowsPerPageOptions={defaultRowsPerPageOptions}
                component="div"
                count={totalCount}
                rowsPerPage={tableData.rowsPerPage}
                page={tableData.page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleRowsPerPageChange}
                ActionsComponent={TablePaginationActions}
              />

              <FormControlLabel
                label="Dense"
                sx={{ px: 3, py: 1.5, top: 0, position: { md: 'absolute' } }}
                control={
                  <Switch
                    checked={tableData.dense}
                    onChange={tableData.onChangeDense}
                  />
                }
              />
            </Box>
            {/* MODALS */}
            {isSendInvoiceModalOpen ? (
              <SendInvoiceModal
                open={isSendInvoiceModalOpen}
                onClose={() => setSendInvoiceModalOpen(false)}
              />
            ) : null}
            {viewInvoiceModalData.open && viewInvoiceModalData.invoice ? (
              <ViewInvoiceModal
                open={viewInvoiceModalData.open}
                onClose={() => setViewInvoiceModalData({ open: false })}
                invoice={viewInvoiceModalData.invoice}
              />
            ) : null}
            {editInvoiceModalData.open && editInvoiceModalData.invoice ? (
              <EditInvoiceStatusModal
                open={editInvoiceModalData.open}
                onClose={() => setEditInvoiceModalData({ open: false })}
                invoice={editInvoiceModalData.invoice}
              />
            ) : null}
            {viewAdminNotesModalData.open &&
            viewAdminNotesModalData.invoice &&
            selectedInvoiceForAdminNotesModal ? (
              <AdminNotesModal
                open={viewAdminNotesModalData.open}
                invoice={selectedInvoiceForAdminNotesModal}
                onClose={() =>
                  setViewAdminNotesModalData({
                    open: false,
                  })
                }
              />
            ) : null}
          </CardContent>
        </Card>
      </div>
    </Fade>
  );
};

export default InvoicesTab;
